import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { StatsBand } from "@/components/StatsBand";
import { FAQSection } from "@/components/FAQSection";
import { ASSETS } from "@/data/content";
import { ABOUT_PAGE, ABOUT_FAQ } from "@/data/aboutContent";
import { Target, Telescope } from "lucide-react";

/* Scroll-reveal wrapper (mirrors the ss3-rv behavior deployed to WP) */
const Reveal = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setOn(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && (setOn(true), obs.disconnect())),
      { threshold: 0.2 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ease-out ${on ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

export default function AboutPage() {
  const A = ABOUT_PAGE;
  return (
    <div data-testid="about-page" className="bg-white">
      <SiteHeader />
      <main>
        {/* Hero */}
        <section
          data-testid="about-hero"
          className="relative overflow-hidden bg-[#0b0b0c] hero-noise"
        >
          <img src={A.hero.bg} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(90deg, rgba(2,2,2,0.85) 0%, rgba(2,2,2,0.6) 50%, rgba(2,2,2,0.85) 100%)" }}
          />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 min-h-[520px] md:min-h-[560px] flex items-center justify-center pt-32 pb-16 md:pt-36 md:pb-20">
            <div className="text-center max-w-3xl mx-auto">
              <p className="font-nunito text-[11px] sm:text-xs font-extrabold uppercase tracking-[0.3em] text-[#ff6b63] mb-5">
                {A.hero.eyebrow}
              </p>
              <h1 data-testid="about-hero-title" className="font-nunito font-extrabold text-white text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.08] tracking-tight">
                {A.hero.titlePre}
                <span className="text-[#ee2e24]">{A.hero.titleAccent}</span>
              </h1>
              <p className="mt-5 text-[#dedede] text-sm sm:text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
                {A.hero.sub}
              </p>
              <div className="mt-8 flex items-center justify-center gap-6" data-testid="about-trust-badges">
                <img src={ASSETS.badgeRecommended} alt="Slingshot SEO recommended company" className="h-16 sm:h-20 w-auto drop-shadow-[0_8px_20px_rgba(0,0,0,0.5)]" loading="lazy" />
                <img src={ASSETS.badgeInc500} alt="Inc. 500 List" className="h-16 sm:h-20 w-auto drop-shadow-[0_8px_20px_rgba(0,0,0,0.5)]" loading="lazy" />
              </div>
            </div>
          </div>
        </section>

        {/* Belief statement */}
        <section data-testid="about-belief" className="bg-white py-16 md:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <Reveal>
              <h2 className="font-nunito text-2xl md:text-[1.9rem] font-extrabold text-[#383838] leading-snug heading-redul">
                {A.belief.heading}
              </h2>
              <p className="mt-6 text-[15px] text-[#6e6e6e] leading-relaxed">{A.belief.body}</p>
            </Reveal>
            <Reveal delay={150}>
              <img
                src={A.belief.image}
                alt="Slingshot SEO team collaborating"
                className="w-full h-72 md:h-80 object-cover rounded-[14px] shadow-[0_14px_40px_rgba(2,2,2,0.12)]"
                loading="lazy"
              />
            </Reveal>
          </div>
        </section>

        {/* Timeline */}
        <section data-testid="about-timeline" className="bg-[#fafafa] border-y border-[#f1f1f1] py-16 md:py-22">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <p className="text-center font-nunito text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#ee2e24]">
              Our story
            </p>
            <h2 className="mt-3 text-center font-nunito text-2xl sm:text-3xl font-extrabold text-[#383838] heading-redul-center">
              Two decades of knowing the rules first
            </h2>
            <div className="mt-14 relative pl-8 sm:pl-10">
              <span className="absolute left-[7px] sm:left-[11px] top-1 bottom-1 w-[2px] bg-gradient-to-b from-[#ee2e24] via-[#ee2e24]/40 to-[#ee2e24]/10" aria-hidden="true" />
              {A.timeline.map((item, i) => (
                <Reveal key={item.year} delay={i * 80} className="relative pb-10 last:pb-0">
                  <span className="absolute -left-8 sm:-left-10 top-1 h-4 w-4 rounded-full bg-white border-[3px] border-[#ee2e24] shadow-[0_0_0_4px_rgba(238,46,36,0.12)]" aria-hidden="true" />
                  <div data-testid={`about-timeline-item-${i + 1}`}>
                    <span className="inline-flex items-center rounded-full bg-[#ee2e24]/[0.08] border border-[#ee2e24]/20 px-3 py-1 font-nunito text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#ee2e24]">
                      {item.year}
                    </span>
                    <h3 className="mt-3 font-nunito text-lg font-extrabold text-[#383838]">{item.title}</h3>
                    <p className="mt-2 text-sm text-[#6e6e6e] leading-relaxed">{item.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Philosophy + values */}
        <section data-testid="about-values" className="bg-white py-16 md:py-22">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="max-w-3xl">
              <p className="font-nunito text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#ee2e24]">
                What we believe
              </p>
              <h2 className="mt-3 font-nunito text-2xl sm:text-3xl font-extrabold text-[#383838] heading-redul">
                The good guys still win. The arena just got bigger.
              </h2>
              {A.philosophy.map((p, i) => (
                <p key={i} className="mt-5 text-[15px] text-[#6e6e6e] leading-relaxed">{p}</p>
              ))}
            </div>
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {A.values.map((v, i) => {
                const Icon = v.icon;
                return (
                  <Reveal key={v.title} delay={i * 80}>
                    <div
                      data-testid={`about-value-card-${i + 1}`}
                      className="h-full rounded-[12px] border border-[#ececec] bg-white p-6 shadow-[0_6px_18px_rgba(2,2,2,0.04)] hover:-translate-y-1 hover:shadow-[0_14px_40px_rgba(2,2,2,0.1)] hover:border-[#ee2e24]/30 transition-transform duration-200"
                    >
                      <div className="h-11 w-11 rounded-[10px] bg-[#ee2e24]/[0.08] flex items-center justify-center">
                        <Icon size={20} strokeWidth={1.75} className="text-[#ee2e24]" />
                      </div>
                      <h3 className="mt-4 font-nunito text-[15px] font-extrabold text-[#383838]">{v.title}</h3>
                      <p className="mt-2 text-[13px] text-[#6e6e6e] leading-relaxed">{v.body}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section data-testid="about-mission-vision" className="bg-[#0b0b0c] py-16 md:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Reveal>
              <div className="h-full rounded-[14px] border border-white/10 bg-white/[0.05] p-8">
                <div className="flex items-center gap-3">
                  <span className="h-10 w-10 rounded-full border border-white/20 bg-white/[0.06] flex items-center justify-center">
                    <Target size={18} strokeWidth={1.75} className="text-[#ee2e24]" />
                  </span>
                  <h3 className="font-nunito text-sm font-extrabold uppercase tracking-[0.24em] text-white">Our Mission</h3>
                </div>
                <p className="mt-5 text-[15px] md:text-base text-[#ececec] leading-relaxed">{A.mission}</p>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div className="h-full rounded-[14px] border border-white/10 bg-white/[0.05] p-8">
                <div className="flex items-center gap-3">
                  <span className="h-10 w-10 rounded-full border border-white/20 bg-white/[0.06] flex items-center justify-center">
                    <Telescope size={18} strokeWidth={1.75} className="text-[#ee2e24]" />
                  </span>
                  <h3 className="font-nunito text-sm font-extrabold uppercase tracking-[0.24em] text-white">Our Vision</h3>
                </div>
                <p className="mt-5 text-[15px] md:text-base text-[#ececec] leading-relaxed">{A.vision}</p>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Full-service mix */}
        <section data-testid="about-mix" className="bg-white py-16 md:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <Reveal>
                <img
                  src={A.culture.image}
                  alt="Slingshot SEO strategists at work"
                  className="w-full h-72 md:h-80 object-cover rounded-[14px] shadow-[0_14px_40px_rgba(2,2,2,0.12)]"
                  loading="lazy"
                />
              </Reveal>
              <div>
                <h2 className="font-nunito text-2xl sm:text-3xl font-extrabold text-[#383838] heading-redul">
                  {A.mix.heading}
                </h2>
                <p className="mt-5 text-[15px] text-[#6e6e6e] leading-relaxed">{A.mix.sub}</p>
                <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {A.mix.items.map((m) => {
                    const Icon = m.icon;
                    return (
                      <div key={m.label} className="flex items-start gap-3.5 rounded-[12px] border border-[#ececec] bg-[#fafafa] p-4">
                        <span className="shrink-0 h-10 w-10 rounded-[10px] bg-white border border-[#ececec] flex items-center justify-center">
                          <Icon size={18} strokeWidth={1.75} className="text-[#ee2e24]" />
                        </span>
                        <div>
                          <h4 className="font-nunito text-[13px] font-extrabold text-[#383838]">{m.label}</h4>
                          <p className="mt-1 text-[12px] text-[#8a8a8a] leading-snug">{m.line}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats (shared band) */}
        <StatsBand />

        {/* Awards */}
        <section data-testid="about-awards" className="bg-white py-14 md:py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
            <p className="font-nunito text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#9a9a9a] mb-6">
              Awards &amp; Recognition
            </p>
            <img
              src={ASSETS.awardsStrip}
              alt="Slingshot SEO awards and recognition"
              className="mx-auto w-full max-w-4xl h-auto"
              loading="lazy"
            />
          </div>
        </section>

        {/* FAQ + schema */}
        <FAQSection heading={ABOUT_FAQ.heading} items={ABOUT_FAQ.items} injectSchema />

        {/* CTA band */}
        <section data-testid="about-cta" className="bg-[#fafafa] border-t border-[#f1f1f1] py-16 md:py-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="font-nunito text-2xl sm:text-3xl font-extrabold text-[#383838]">{A.cta.heading}</h2>
            <p className="mt-4 text-[15px] text-[#6e6e6e] leading-relaxed">{A.cta.sub}</p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <Link
                to="/#visibility-audit"
                data-testid="about-cta-primary-button"
                className="w-full sm:w-auto inline-flex justify-center items-center rounded-[10px] bg-[#ee2e24] px-7 py-3.5 font-nunito text-[13px] font-extrabold uppercase tracking-[0.12em] text-white shadow-[0_14px_40px_rgba(238,46,36,0.3)] hover:bg-[#d92820] active:scale-[0.98] transition-colors duration-150"
              >
                {A.cta.primaryText}
              </Link>
              <a
                href={A.cta.secondaryHref}
                data-testid="about-cta-secondary-button"
                className="w-full sm:w-auto inline-flex justify-center items-center rounded-[10px] border border-[#d9d9d9] px-7 py-3.5 font-nunito text-[13px] font-extrabold uppercase tracking-[0.12em] text-[#383838] hover:border-[#ee2e24] hover:text-[#ee2e24] transition-colors duration-150"
              >
                {A.cta.secondaryText}
              </a>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
